import { Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { BarChart, PieChart } from '@mui/x-charts';
import { useDrawingArea } from '@mui/x-charts/hooks';
import React from 'react';
import { MyMLabel } from '../../ui-elements/MyMLabel';
import { utility } from '../../utility';

// MyMine Version2.0 SurveyResult画面追加
/** 
 * Option回答結果棒グラフ Component
 */
export const SurveyResultOptionBarGraph = ({ controlSettings, answerResultList, optionList, screenSettings }) => {
  // 2025.7.7 MyMine Version3.0対応 Modify Start
  const dataset = optionList ?
    // Mutiple Selection, Single Selectoionの場合
    optionList
      .map((optionInfo) => {
        return {
          value: optionInfo.OptionId,
          text: optionInfo.OptionText,
          answerCount: answerResultList.flatMap(x => x.AnswerSelectOptionList).filter(o => o === optionInfo.OptionId).length,
        };
      })
    :
    // Scaleの場合
    Array.from({ length: 10 }, (_, index) => index + 1)
      .map(x => {
        return {
          value: x,
          answerCount: answerResultList.filter(y => y.AnswerOption === x).length
        };
      })

  return (
    answerResultList.length === 0
      ?
      <Box
        sx={{
          height: 200, width: 250,
        }}
        className='d-flex justify-content-center align-items-center'>
        <MyMLabel>{controlSettings.LabelList.lblNoAnswered}</MyMLabel>
      </Box>
      :
      // 2025.7.7 MyMine Version3.0対応 Modify Start
      <BarChart
        sx={{
          '& .MuiBarLabel-root, & .MuiChartsAxis-tickLabel': {
            fontSize: '17px !important',
          },
        }}
        dataset={dataset}
        xAxis={[
          optionList ?
            {
              scaleType: 'band',
              dataKey: optionList ? 'text' : 'value',
              valueFormatter: (label) => utility.convertEllipsisFormat(label.toString(), screenSettings.OptionTextMaxLength),
              hideTooltip: true,
              colorMap: {
                type: 'ordinal',
                colors: SelectionOtionPieGrapColorList,
                unknownColor: '#ccc'
              }
            }
            :
            {
              scaleType: 'band',
              dataKey: optionList ? 'text' : 'value',
              valueFormatter: (label) => utility.convertEllipsisFormat(label.toString(), screenSettings.OptionTextMaxLength),
              hideTooltip: true,
              colorMap: {
                type: 'piecewise',
                colors: ['#0088FE', '#00C49F', '#FFBB28'],
                thresholds: [4, 7]
              }

            }
        ]}
        series={[{ dataKey: 'answerCount' }]}
        colors={SelectionOtionPieGrapColorList}
        height={300}
        barLabel="value"
        leftAxis={null}
        tooltip={{ trigger: 'none' }}
      >
      </BarChart >
      // 2025.7.7 MyMine Version3.0対応 Modify End
  );
  // 2025.7.7 MyMine Version3.0対応 Modify End
}

/** Scale円グラフのグラフ設定定義 */
const OptionPieGraphDataRangeInfo = [{ from: 1, to: 3, color: '#0088FE' }, { from: 4, to: 6, color: '#00C49F' }, { from: 7, to: 10, color: '#FFBB28' }];

// 2025.7.7 MyMine Version3.0対応 Add Start
/** Mutiple Selection, Single Selectoion円グラフの色コード定義 */
const SelectionOtionPieGrapColorList = [
  '#EE5A24', '#019352', '#0097E6', '#FFD93D', '#EF1E51',
  '#FF6B6B', '#6BCB77', '#4D96FF', '#F79F1F', '#833471',
  '#ED4D01', '#78e08f', '#008083', '#EE5A24', '#B53471', 
  '#A3CB38', '#60a3bc', '#FA983A', '#EB2F06', '#ED4C67',
  '#006266', '#5758BB', '#3B3B98', '#BFD300', '#B71540'
];
// 2025.7.7 MyMine Version3.0対応 Add End

/** 
 * Option回答結果円グラフ Component
 */
export const SurveyResultOptionPieGraph = ({ controlSettings, answerResultList, onClickGraph, optionList }) => {

  // 2025.7.7 MyMine Version3.0対応 Modify Start
  const data = optionList ?
    // Mutiple Selection, Single Selectoionの場合
    optionList
      .map((optionInfo, i) => {
        return {
          color: SelectionOtionPieGrapColorList[i % 10],
          value: answerResultList?.flatMap(x => x.AnswerSelectOptionList).filter(o => o === optionInfo.OptionId).length ?? 0
        };
      })
    :
    // Scaleの場合
    OptionPieGraphDataRangeInfo.map(x => {
      return {
        color: x.color,
        value: answerResultList.filter(ans => ans.AnswerOption >= x.from && ans.AnswerOption <= x.to).length
      }
    })

  const StyledText = styled('text')(({ theme }) => ({
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fontSize: '1.2rem',
  }));

  const PieCenterLabel = ({ children }) => {
    const { width, height, left, top } = useDrawingArea();
    return (
      <StyledText x={left + width / 2} y={top + height / 2}>
        {children}
      </StyledText>
    );
  }

  return answerResultList.length === 0
    ?
    <Box
      sx={{ height: 200, width: 250 }}
      className='d-flex justify-content-center align-items-center'>
      <MyMLabel>{controlSettings.LabelList.lblNoAnswered}</MyMLabel>
    </Box>
    :
    <PieChart
      onClick={onClickGraph}
      sx={{
        ':hover': { cursor: onClickGraph ? 'pointer' : 'auto' },
        paddingLeft: 3,
      }}
      series={[
        {
          paddingAngle: 0,
          innerRadius: 50,
          data,
        },
      ]}
      width={300}
      height={200}
      tooltip={{ trigger: 'none' }}

    >
      <PieCenterLabel>{answerResultList.length}</PieCenterLabel>
    </PieChart>
  // 2025.7.7 MyMine Version3.0対応 Modify End
}

/** 
 * Option回答結果円グラフ凡例 Component
 */
export const SurveyResultOptionPieGraphLegend = ({ optionList }) => {
  // 2025.7.7 MyMine Version3.0対応 Modify Start
  return (
    <Stack direction='row' className='pe-2' sx={{ flexWrap: 'wrap' }}>
      {(optionList ?? OptionPieGraphDataRangeInfo).map((x, i) =>
        <Stack alignItems={{ xs: 'center' }} direction={{ xs: 'row', md: 'row' }} sx={{ paddingLeft: { xs: 1, sm: 3, lg: 2 } }} key={i}>
          <Box key={i} className='d-inline-flex'>
            <Box sx={{ backgroundColor: x.color ?? SelectionOtionPieGrapColorList[i % 10], width: 30, height: 20 }} />
          </Box>
          <MyMLabel sx={{ marginLeft: { xs: 1 } }}>{x.OptionText ?? `${x.from}-${x.to}`}</MyMLabel>
        </Stack>
      )}
    </Stack>);
  // 2025.7.7 MyMine Version3.0対応 Modify End

};